import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";

const AnnotationOverlay = ({ containerRef, isFullScreen }) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const isDrawingRef = useRef(false);
  const resizeObserverRef = useRef(null);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const [annotateEnabled, setAnnotateEnabled] = useState(false);
  const [drawMode, setDrawMode] = useState("pen");
  const [strokeColor, setStrokeColor] = useState("#ff0000");
  const [lineWidth, setLineWidth] = useState(3);
  const [overlayOpacity, setOverlayOpacity] = useState(0.0);

  // Initialize and keep canvas in sync with container size
  useEffect(() => {
    const container = containerRef?.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const setupCanvasSize = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // snapshot existing content
      const prevW = canvas.width;
      const prevH = canvas.height;
      let snapshot = null;
      if (prevW && prevH) {
        snapshot = document.createElement("canvas");
        snapshot.width = prevW;
        snapshot.height = prevH;
        const sctx = snapshot.getContext("2d");
        sctx.drawImage(canvas, 0, 0);
      }

      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctxRef.current = ctx;

      if (snapshot) {
        ctx.drawImage(
          snapshot,
          0,
          0,
          prevW,
          prevH,
          0,
          0,
          rect.width,
          rect.height
        );
      }
    };

    setupCanvasSize();
    if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    resizeObserverRef.current = new ResizeObserver(setupCanvasSize);
    resizeObserverRef.current.observe(container);

    return () => {
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    };
  }, [containerRef, isFullScreen]);

  // Apply tool properties
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.globalCompositeOperation =
      drawMode === "eraser" ? "destination-out" : "source-over";
  }, [strokeColor, lineWidth, drawMode]);

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    if (!annotateEnabled) return;
    const canvas = canvasRef.current;
    canvas.setPointerCapture?.(e.pointerId);
    const ctx = ctxRef.current;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = (e.pressure && e.pressure > 0 ? e.pressure : 1) * lineWidth;
    ctx.globalCompositeOperation =
      drawMode === "eraser" ? "destination-out" : "source-over";
    const { x, y } = getCanvasPos(e);
    isDrawingRef.current = true;
    lastPointRef.current = { x, y };
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!annotateEnabled || !isDrawingRef.current) return;
    const ctx = ctxRef.current;
    const list = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
    for (const ev of list) {
      const { x, y } = getCanvasPos(ev);
      ctx.lineWidth =
        (ev.pressure && ev.pressure > 0 ? ev.pressure : 1) * lineWidth;
      ctx.lineTo(x, y);
      lastPointRef.current = { x, y };
    }
    ctx.stroke();
    e.preventDefault();
  };

  const onPointerUp = (e) => {
    if (!annotateEnabled) return;
    const ctx = ctxRef.current;
    if (isDrawingRef.current) ctx.closePath();
    isDrawingRef.current = false;
    e.preventDefault();
  };

  const clearAnnotations = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  };

  return (
    <>
      <div className="absolute top-14 right-2 flex space-x-2 z-50">
        <button
          onClick={() => setAnnotateEnabled((v) => !v)}
          className={clsx(
            "p-2 w-10 h-10 rounded-full shadow-md text-gray-700 transition-colors",
            "bg-gray-200 hover:bg-gray-300",
            "dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300",
            "focus:outline-none focus:ring-0 flex items-center justify-center"
          )}
          aria-label="Toggle annotations"
          title="Toggle annotations"
        >
          <span className="text-base">✏️</span>
        </button>
        {annotateEnabled && (
          <div className="flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur px-2 py-1 rounded-full border border-gray-300 dark:border-gray-700">
            <button
              onClick={() => setDrawMode("pen")}
              className={clsx(
                "px-2 py-1 rounded text-sm",
                drawMode === "pen"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              )}
              title="Pen"
            >
              Pen
            </button>
            <button
              onClick={() => setDrawMode("eraser")}
              className={clsx(
                "px-2 py-1 rounded text-sm",
                drawMode === "eraser"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              )}
              title="Eraser"
            >
              Erase
            </button>
            <input
              type="color"
              value={strokeColor}
              onChange={(e) => setStrokeColor(e.target.value)}
              title="Stroke color"
              className="w-8 h-8 p-0 border border-gray-300 dark:border-gray-600 rounded"
            />
            <input
              type="range"
              min="1"
              max="12"
              value={lineWidth}
              onChange={(e) => setLineWidth(Number(e.target.value))}
              title="Line width"
            />
            <div className="flex items-center space-x-1">
              <span className="text-xs text-gray-600 dark:text-gray-300">
                Dim
              </span>
              <input
                type="range"
                min="0"
                max="0.9"
                step="0.05"
                value={overlayOpacity}
                onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                title="Background opacity"
              />
            </div>
            <button
              onClick={clearAnnotations}
              className="px-2 py-1 rounded text-sm bg-red-600 text-white hover:bg-red-700"
              title="Clear annotations"
            >
              Clear
            </button>
          </div>
        )}
      </div>
      <div
        className="absolute inset-0 z-30"
        style={{
          backgroundColor: `rgba(0,0,0,${overlayOpacity})`,
          pointerEvents: "none",
        }}
      />
      <canvas
        ref={canvasRef}
        className={clsx(
          "absolute inset-0 z-40 touch-none select-none",
          annotateEnabled ? "cursor-crosshair" : "pointer-events-none"
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
      />
    </>
  );
};

export default AnnotationOverlay;
